import {
  isFssAskConversationState,
  isFssAskQueryConversationState,
  isFssAskWithFileConversationState,
  isFssAskWithFolderConversationState,
  type ConversationRouterState,
} from "@mirage/service-conversation/router_state_types";
import type {
  Conversation,
  ConversationFSSClientScenarioSources,
} from "@mirage/service-conversation/types";
import {
  convertFssEntityScopeToChatSource,
  isFSSEntityWithFileId,
} from "@mirage/service-conversation/utils/convertFssEntityToChatSource";
import type {
  FSSFile,
  FSSFolder,
} from "@mirage/service-fss-bridge/service/bridge_types";
import { tagged } from "@mirage/service-logging";
import { useEffect, useMemo } from "react";
import { useScopedSources } from "./attachments/useManuallyAddedSources";

const logger = tagged("useFssConversationDetails");

export const useFssConversationDetails = (
  routerState?: ConversationRouterState,
  conversation?: Conversation,
) => {
  const { setScopedSources } = useScopedSources();

  const fssConversationMetadata = useMemo(() => {
    return (
      parseFssConversation(conversation) || parseFssRouterState(routerState)
    );
  }, [routerState, conversation?.clientContext]);

  useEffect(() => {
    if (fssConversationMetadata?.sources) {
      if (Array.isArray(fssConversationMetadata.sources)) {
        setScopedSources(
          fssConversationMetadata.sources.map((source) =>
            convertFssEntityScopeToChatSource(source, "scope"),
          ),
        );
      } else if (isFSSEntityWithFileId(fssConversationMetadata.sources)) {
        setScopedSources([
          convertFssEntityScopeToChatSource(
            fssConversationMetadata.sources,
            "scope",
          ),
        ]);
      }
    }
  }, [fssConversationMetadata?.sources, setScopedSources]);

  return fssConversationMetadata;
};

export const parseFssConversation = (conversation?: Conversation) => {
  if (!conversation?.clientContext?.clientScenario) {
    return null;
  }

  let sources: FSSFile[] | FSSFolder | undefined;
  try {
    const clientScenarioSources: {
      sources: ConversationFSSClientScenarioSources;
    } =
      // if clientScenarioId is empty, don't just fail, process the type accordingly
      JSON.parse(conversation.clientContext.clientScenarioId || "{}");
    switch (conversation.clientContext.clientScenario) {
      case "fss-stack":
        sources = clientScenarioSources.sources as FSSFile[];
        break;
      case "fss-file-preview":
        sources = [clientScenarioSources.sources as FSSFile];
        break;
      case "fss-folder":
        sources = clientScenarioSources.sources as FSSFolder;
        break;
      case "fss-serp-answer":
      case "fss-serp-follow-up":
      case "fss-unscoped":
        // by default for fss conversations, we should empty the scoped source
        sources = [];
        break;
      default:
        return null;
    }
  } catch (e) {
    logger.error("Error parsing FSS conversation", { error: e });
    return null;
  }
  return { sources };
};

export const parseFssRouterState = (routerState?: ConversationRouterState) => {
  if (!routerState) {
    return null;
  }

  if (
    isFssAskQueryConversationState(routerState) ||
    isFssAskConversationState(routerState) ||
    isFssAskWithFileConversationState(routerState) ||
    isFssAskWithFolderConversationState(routerState)
  ) {
    let sources: FSSFile[] | FSSFolder | undefined;
    switch (routerState?.scenarioMeta?.type) {
      case "fss_file_preview":
        sources = [routerState.scenarioMeta.file];
        break;
      case "fss_folder":
        sources = routerState.scenarioMeta.folder;
        break;
      case "fss_stack":
        sources = routerState.scenarioMeta.files;
        break;
      case "fss_serp_query":
        // fss_serp_query passes `query` in scenarioMeta
        // we don't need to do anything here
        break;
      default:
        // by default for fss conversations, we should empty the scoped source
        // to remove any previous scopes
        sources = [];
        break;
    }
    return { sources };
  }

  return null;
};
